import React, { useEffect, useRef } from "react";
import {
  Box, CircularProgress, MenuItem, Select, TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CallIcon from "@mui/icons-material/Call";
import { CP_SOURCING_OUTCOMES } from "../../../Services/CpSourcingApis";
import { TIER_META } from "../cpSourcingTheme";
import { fmtDateTime } from "../cpSourcingFormat";
import css from "./cpSourcingMobile.module.css";
import "./tokens.css";

// 16px on every input stops iOS Safari zooming the page on focus.
const fieldSx = {
  mb: "12px",
  background: "#ffffff",
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontFamily: "Inter-Medium",
    "& fieldset": { borderColor: "#d0d5dd" },
  },
  "& .MuiSelect-select, & input, & textarea": {
    fontSize: "16px",
    lineHeight: "22px",
  },
};

// Full-screen CP detail for phones. Presentational only — CpDetailDrawer owns
// the history fetch, the outcome/remark state and the save, so mobile and
// desktop cannot drift apart in behaviour.
export default function CpDetailMobile({
  cp,
  onClose,
  onCall,
  history,
  loading,
  outcome,
  onOutcomeChange,
  remark,
  onRemarkChange,
  saving,
  onSave,
}) {
  const meta = TIER_META[cp.tier] || TIER_META.neverActive;

  // The body is its own scroll container, so window.scrollTo would do nothing.
  const bodyRef = useRef(null);
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [cp.cpId]);

  // This page overlays the still-mounted list. Without locking the body, a drag
  // that starts on the header or the action bar scrolls the list underneath.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, []);

  return (
    <div className={`cpSourcingMobileRoot ${css.detailRoot}`}>
      <div className={css.detailHeader}>
        <button
          type="button"
          className={css.detailBack}
          aria-label="Back"
          onClick={onClose}
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </button>
        <div className={css.detailHeaderText}>
          <div className={css.detailHeaderTitle}>{cp.cpName || "—"}</div>
          <div className={css.detailHeaderId}>#{cp.cpDisplayId}</div>
        </div>
      </div>

      <div className={css.detailBody} ref={bodyRef}>
        <div className={css.detailCard}>
          <div className={css.detailIdentity}>
            <span
              className={css.statusPill}
              style={{
                background: meta.bg,
                color: meta.color,
                border: `1px solid ${meta.border}`,
                fontWeight: 600,
              }}
            >
              {meta.label}
            </span>
            {cp.daysDormant !== null && cp.daysDormant !== undefined && (
              <span className={css.detailDormant}>
                {cp.daysDormant} days dormant
              </span>
            )}
          </div>

          <div className={css.kv}>
            <span className={css.kvKey}>Contact</span>
            <span className={css.kvValue}>
              {cp.contactPersonName || "—"}
              {cp.phone ? ` · ${cp.phone}` : " · no number"}
            </span>
          </div>
          <div className={css.kv} style={{ marginBottom: 0 }}>
            <span className={css.kvKey}>CP Sourcing</span>
            <span className={css.kvValue}>{cp.ownerName || "—"}</span>
          </div>
        </div>

        {/* Add remark */}
        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>Log this call</div>
          <Select
            size="small"
            displayEmpty
            fullWidth
            value={outcome}
            onChange={(e) => onOutcomeChange(e.target.value)}
            sx={fieldSx}
          >
            <MenuItem value="">
              <em>Select outcome…</em>
            </MenuItem>
            {CP_SOURCING_OUTCOMES.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={3}
            placeholder="What was discussed…"
            value={remark}
            onChange={(e) => onRemarkChange(e.target.value)}
            sx={fieldSx}
          />
          <button
            type="button"
            className={`${css.btn} ${css.btnPrimary}`}
            style={{ width: "100%" }}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save remark"}
          </button>
        </div>

        {/* History */}
        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>Discussion history</div>
          {loading && (
            <Box sx={{ textAlign: "center", py: "16px" }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && history.length === 0 && (
            <div style={{ fontSize: 14, color: "#667085" }}>
              No calls logged yet — this CP has never been contacted.
            </div>
          )}
          {!loading &&
            history.map((h) => (
              <div key={h.cpSourcingActivityLogId} className={css.historyCard}>
                <div className={css.historyTop}>
                  <span className={css.historyOutcome}>{h.outcome}</span>
                  <span className={css.historyTime}>
                    {fmtDateTime(h.created_at)}
                  </span>
                </div>
                {h.remark && <div className={css.historyRemark}>{h.remark}</div>}
                {h.userName && (
                  <div className={css.historyBy}>by {h.userName}</div>
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Sticky action bar — Call stays reachable without scrolling past the
          history. */}
      <div className={css.detailActions}>
        <button
          type="button"
          className={`${css.btn} ${css.btnPrimary}`}
          onClick={onCall}
          disabled={!cp.phone}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <CallIcon sx={{ fontSize: 18 }} />
          {cp.phone ? "Call" : "No number"}
        </button>
      </div>
    </div>
  );
}
